import {
  DOCUMENT_MAX_BYTES,
  KB_CHUNK_MAX_CHARS,
  KB_SECTION_MAX_CHARS,
  PROMPT_MAX_BYTES,
  type BotSettings,
  type DocumentsResponse,
  type KbResponse,
  type KbRevisionsResponse,
  type KbWriteResponse,
  type LegacyPromptsResponse,
  type PreviewResponse,
  type PromptName,
  type PromptSummary,
  type PromptVersion,
  type PromptsResponse,
  type PublishResponse,
} from '@platform/contracts';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  cn,
} from '@platform/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  Bot,
  CloudUpload,
  FileText,
  FlaskConical,
  History,
  Info,
  Lock,
  RotateCcw,
  Save,
  Send,
  Paperclip,
  ScrollText,
  Upload,
  Sparkles,
  Download,
  Maximize2,
  Trash2,
} from 'lucide-react';
import { type SubmitEvent, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { fd } from '../components/form';
import { ConfirmDialog } from '../components/row-actions';
import { Guard, PageHeader } from '../components/page';
import { ViewerDialog } from '../components/viewer';
import { Loadable, TenantPicker, useTenantPick } from '../components/states';
import { ApiError, api, errorMessage } from '../lib/api';
import { fmtBytes, fmtDateTime } from '../lib/console';
import { type DiffLine, diffLines, diffStats } from '../lib/diff';
import { canAccess } from '../lib/modules';
import { useMe } from '../lib/queries';
import { fileToBase64 } from '../lib/format';

// ------------------------------------------------------------------ diff

export function DiffView({ before, after }: { before: string; after: string }) {
  const lines = useMemo(() => diffLines(before, after), [before, after]);
  const stats = diffStats(lines);
  if (stats.added === 0 && stats.removed === 0) {
    return <p className="text-sm text-muted-foreground">Sin cambios respecto de la publicada.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-muted-foreground">
        <span className="text-success">+{stats.added}</span> ·{' '}
        <span className="text-destructive">−{stats.removed}</span> líneas
      </p>
      <pre
        aria-label="Diferencias con la versión publicada"
        className="max-h-96 overflow-auto rounded-md border bg-card p-2 font-mono text-xs leading-5"
      >
        {lines.map((l: DiffLine, i) => (
          <div
            key={i}
            className={cn(
              'px-1 whitespace-pre-wrap',
              l.kind === 'add' && 'bg-success/15',
              l.kind === 'del' && 'bg-destructive/12 line-through decoration-destructive/40',
            )}
          >
            <span aria-hidden className="mr-2 inline-block w-3 select-none text-muted-foreground">
              {l.kind === 'add' ? '+' : l.kind === 'del' ? '−' : ' '}
            </span>
            {l.text || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}

const PUSH_LABEL: Record<string, string> = {
  ok: 'En uso por el asistente',
  skipped: 'Guardada (aún no la usa el asistente)',
  failed: 'No se pudo activar',
  pending: 'Activándose',
};

/** Quién hizo la versión, en lenguaje llano (las importadas no tienen usuario del panel). */
export function versionAuthor(v: PromptVersion): string {
  if (v.origin === 'import') return 'Importado de n8n';
  if (v.origin === 'external') return 'Cambio hecho fuera del panel';
  return v.authorEmail ?? 'sistema';
}

/** Orden en que trabaja la IA, y qué hace cada texto dicho para el cliente. */
const PIPELINE: { title: string; names: PromptName[] }[] = [
  { title: 'Antes del primer email', names: ['extractor', 'qualifier', 'personalizer'] },
  {
    title: 'Cuando un contacto responde',
    names: ['first_contact', 'first_reply_instructions', 'answerer'],
  },
];

const CAPABILITY: Record<PromptName, string> = {
  extractor: 'Lee la web de cada tienda nueva y saca sus datos.',
  qualifier: 'Decide si la tienda encaja con tu negocio y le pone una puntuación.',
  personalizer: 'Escribe la primera frase del email para cada tienda que encaja.',
  first_contact:
    'Cuando alguien responde pidiendo información, le contesta con un email de bienvenida y el dossier adjunto.',
  answerer:
    'Responde las preguntas de los contactos con la base de conocimiento; si no está seguro, te pasa la conversación a ti.',
  first_reply_instructions:
    'En la primera respuesta a alguien interesado, el asistente sigue estas instrucciones extra y adjunta el dossier.',
};

/** Nombre del flujo cuando el primer contacto lo contesta el asistente (modo `bot_answer`). */
export const BOT_ANSWER_FLOW = 'Respuesta del asistente + dossier';

/** Textos que la empresa usa de verdad (los que tienen alguna versión en el panel). */
function usedPrompts(data: PromptsResponse): PromptSummary[] {
  return data.prompts.filter((p) => p.versions > 0);
}

function usePrompts(tenantId: string) {
  return useQuery({
    queryKey: ['prompts', tenantId],
    queryFn: () => api<PromptsResponse>(`/api/tenants/${tenantId}/prompts`),
  });
}

function useDocuments(tenantId: string) {
  return useQuery({
    queryKey: ['documents', tenantId],
    queryFn: () => api<DocumentsResponse>(`/api/tenants/${tenantId}/documents`),
  });
}

/** Resumen en lenguaje llano de lo que hace hoy la IA de la empresa. */
function AiOverview({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const q = usePrompts(tenantId);
  const docs = useDocuments(tenantId);
  if (!q.data) return null;
  // El texto de primer contacto puede estar cargado con la respuesta automática apagada en n8n.
  const firstContactOff = docs.data?.dossierBot?.firstContactEnabled === false;
  const botAnswer = docs.data?.dossierBot?.firstContactMode === 'bot_answer';
  const used = new Set(usedPrompts(q.data).map((p) => p.name));
  if (used.size === 0) return null;
  const hasBot = used.has('answerer');
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle icon={<Sparkles />}>Qué hace hoy la IA de {tenantName}</CardTitle>
        {!hasBot ? (
          <CardDescription>
            {tenantName} no tiene asistente de respuestas: su IA trabaja antes del primer email,
            calificando tiendas y personalizando el mensaje. Las respuestas de los contactos las
            contestas tú.
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        {PIPELINE.map((step) => {
          const names = step.names.filter((n) => used.has(n));
          if (names.length === 0) return null;
          return (
            <div key={step.title}>
              <h3 className="mb-1 text-sm font-semibold">{step.title}</h3>
              <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-muted-foreground">
                {names.map((n) => (
                  <li key={n}>
                    {n === 'first_contact' && botAnswer
                      ? `${BOT_ANSWER_FLOW}: cuando alguien responde pidiendo información, el asistente le contesta con la base de conocimiento y adjunta el dossier.`
                      : CAPABILITY[n]}
                    {n === 'first_contact' && firstContactOff
                      ? ' (Todavía no está activada: el texto está guardado para revisarlo.)'
                      : null}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ editor de un prompt

function PromptEditor({
  tenantId,
  summary,
  canWrite: roleCanWrite,
  requirePreview,
  onDone,
}: {
  tenantId: string;
  summary: PromptSummary;
  canWrite: boolean;
  requirePreview: boolean;
  onDone: () => void;
}) {
  const canWrite = roleCanWrite && summary.editable;
  const qc = useQueryClient();
  const base = summary.draft ?? summary.published;
  const [content, setContent] = useState(base?.content ?? '');
  const [note, setNote] = useState('');
  const [draft, setDraft] = useState<PromptVersion | null>(summary.draft);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [force, setForce] = useState(false);
  const areaId = useId();
  const noteId = useId();
  const bytes = new TextEncoder().encode(content).length;
  const dirty = content !== (draft?.content ?? summary.published?.content ?? '');
  const refresh = () => qc.invalidateQueries({ queryKey: ['prompts', tenantId] });

  const save = useMutation({
    mutationFn: () =>
      api<PromptVersion>(`/api/tenants/${tenantId}/prompts/${summary.name}/drafts`, {
        method: 'POST',
        body: { content, note: note || undefined, baseVersionId: summary.published?.id ?? null },
      }),
    onSuccess: (v) => {
      setDraft(v);
      setPreview(null);
      setNote('');
      toast.success(`Borrador v${String(v.version)} guardado`);
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const runPreview = useMutation({
    mutationFn: (id: string) =>
      api<PreviewResponse>(`/api/tenants/${tenantId}/prompt-versions/${id}/preview`, {
        method: 'POST',
      }),
    onSuccess: (r) => {
      setPreview(r);
      void refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const publish = useMutation({
    mutationFn: (id: string) =>
      api<PublishResponse>(`/api/tenants/${tenantId}/prompt-versions/${id}/publish`, {
        method: 'POST',
        body: { expectedPublishedId: summary.published?.id ?? null, force },
      }),
    onSuccess: (r) => {
      toast.success(`Publicada v${String(r.version.version)}`, {
        description: r.legacy.message ?? undefined,
      });
      void refresh();
      onDone();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'legacy_prompt_drift') {
        toast.error(err.message);
        setForce(true);
        return;
      }
      toast.error(errorMessage(err));
    },
  });

  const needsPreview = requirePreview && draft !== null && !draft.previewedAt && !preview;
  return (
    <div className="flex flex-col gap-4">
      {!summary.editable ? (
        <Callout tone="info" icon={<Lock />} title="Solo lectura">
          {summary.readOnlyReason}
        </Callout>
      ) : !canWrite ? (
        <Callout tone="info" icon={<Lock />} title="Solo lectura">
          Tu rol puede ver los textos de esta empresa pero no editarlos.
        </Callout>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={areaId}>Contenido</Label>
          <span
            className={cn(
              'text-xs',
              bytes > PROMPT_MAX_BYTES ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {fmtBytes(bytes)} / {fmtBytes(PROMPT_MAX_BYTES)}
          </span>
        </div>
        <Textarea
          id={areaId}
          value={content}
          readOnly={!canWrite}
          onChange={(e) => {
            setContent(e.target.value);
          }}
          className="min-h-72 font-mono text-xs"
          spellCheck={false}
        />
      </div>
      {canWrite ? (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex min-w-48 flex-1 flex-col gap-1">
            <Label htmlFor={noteId}>Nota de la versión (opcional)</Label>
            <Input
              id={noteId}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
              placeholder="Qué cambiaste y por qué"
              maxLength={500}
            />
          </div>
          <Button
            onClick={() => {
              save.mutate();
            }}
            disabled={!dirty || save.isPending || bytes > PROMPT_MAX_BYTES}
          >
            <Save /> Guardar borrador
          </Button>
        </div>
      ) : null}
      <div>
        <h4 className="mb-1 text-sm font-semibold">Cambios contra la publicada</h4>
        <DiffView before={summary.published?.content ?? ''} after={content} />
      </div>
      {canWrite && draft && !dirty ? (
        <Card>
          <CardContent className="flex flex-col gap-3 p-4">
            <p className="text-sm">
              Borrador <strong>v{draft.version}</strong> listo.
              {requirePreview ? ' Esta empresa exige probarlo antes de publicar.' : ''}
            </p>
            {preview ? (
              <Callout
                tone={preview.status === 'failed' ? 'destructive' : 'info'}
                icon={<FlaskConical />}
                title="Prueba"
              >
                {preview.message}
              </Callout>
            ) : null}
            {force ? (
              <Callout tone="warning" icon={<Info />} title="El asistente usa otra versión">
                Alguien cambió este texto fuera del panel. Si publicas, se reemplaza.
              </Callout>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  runPreview.mutate(draft.id);
                }}
                disabled={runPreview.isPending}
              >
                <FlaskConical /> Probar
              </Button>
              <Button
                onClick={() => {
                  if (
                    confirm(
                      `¿Publicar la v${String(draft.version)}? El motor la toma en menos de un minuto.`,
                    )
                  ) {
                    publish.mutate(draft.id);
                  }
                }}
                disabled={publish.isPending || needsPreview}
              >
                <Send /> {force ? 'Publicar igual' : 'Publicar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function VersionsList({
  tenantId,
  summary,
  canWrite,
}: {
  tenantId: string;
  summary: PromptSummary;
  canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [show, setShow] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['prompts', tenantId, 'versions', summary.name],
    queryFn: () =>
      api<PromptVersion[]>(`/api/tenants/${tenantId}/prompts/${summary.name}/versions`),
  });
  const rollback = useMutation({
    mutationFn: (id: string) =>
      api<PublishResponse>(`/api/tenants/${tenantId}/prompt-versions/${id}/rollback`, {
        method: 'POST',
        body: { expectedPublishedId: summary.published?.id ?? null },
      }),
    onSuccess: (r) => {
      toast.success(`Volviste a la v${String(r.version.version)}`, {
        description: r.legacy.message ?? undefined,
      });
      void qc.invalidateQueries({ queryKey: ['prompts', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Loadable isLoading={q.isLoading} error={q.error}>
      <ol className="flex flex-col gap-2">
        {(q.data ?? []).map((v) => (
          <li key={v.id} className="rounded-md border p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <strong>v{v.version}</strong>
              <Badge
                variant={
                  v.status === 'published'
                    ? 'success'
                    : v.status === 'draft'
                      ? 'warning'
                      : 'secondary'
                }
              >
                {v.status === 'published'
                  ? 'Publicada'
                  : v.status === 'draft'
                    ? 'Borrador'
                    : 'Anterior'}
              </Badge>
              {v.legacyPushStatus && v.status === 'published' ? (
                <Badge variant={v.legacyPushStatus === 'ok' ? 'outline' : 'secondary'}>
                  {PUSH_LABEL[v.legacyPushStatus]}
                </Badge>
              ) : null}
              <span className="text-xs text-muted-foreground">
                {versionAuthor(v)} · {fmtDateTime(v.createdAt)}
              </span>
              <div className="ml-auto flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setShow(show === v.id ? null : v.id);
                  }}
                >
                  {show === v.id ? 'Ocultar' : 'Ver cambios'}
                </Button>
                {canWrite && summary.editable && v.status === 'archived' ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm(`¿Volver a publicar la v${String(v.version)}?`))
                        rollback.mutate(v.id);
                    }}
                    disabled={rollback.isPending}
                  >
                    <RotateCcw /> Rollback
                  </Button>
                ) : null}
              </div>
            </div>
            {v.note ? <p className="mt-1 text-xs text-muted-foreground">{v.note}</p> : null}
            {v.sourceRef && v.origin !== 'panel' ? (
              <p className="mt-0.5 text-xs text-muted-foreground">Origen: {v.sourceRef}</p>
            ) : null}
            {show === v.id ? (
              <div className="mt-2">
                <DiffView before={summary.published?.content ?? ''} after={v.content} />
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </Loadable>
  );
}

function LegacyStatus({
  tenantId,
  canWrite,
  prompts,
}: {
  tenantId: string;
  canWrite: boolean;
  prompts: PromptSummary[];
}) {
  const isAgency = canAccess(useMe(), 'ops:read');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['prompts', tenantId, 'legacy'],
    queryFn: () => api<LegacyPromptsResponse>(`/api/tenants/${tenantId}/prompts/legacy`),
  });
  const importOne = useMutation({
    mutationFn: (name: string) =>
      api<PromptVersion>(`/api/tenants/${tenantId}/prompts/${name}/import`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Versión en uso importada');
      void qc.invalidateQueries({ queryKey: ['prompts', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  if (q.isLoading || !q.data) return null;
  if (!q.data.available) {
    // Detalle técnico (conexión con el motor): sólo le sirve a la agencia.
    if (!isAgency) return null;
    return (
      <Callout tone="info" icon={<Info />} title="Textos en uso por el asistente">
        {q.data.message}
      </Callout>
    );
  }
  const outOfSync = q.data.prompts.filter((p) => !p.inSync);
  if (outOfSync.length === 0) return null;
  return (
    <Callout
      tone="warning"
      icon={<Info />}
      title="El asistente usa textos que no están en el panel"
    >
      <ul className="mt-1 flex flex-col gap-1">
        {outOfSync.map((p) => (
          <li key={p.name} className="flex flex-wrap items-center gap-2">
            <code>{p.name}</code>
            <span className="text-xs">
              (editado {fmtDateTime(p.updatedAt)} por {p.updatedBy ?? '—'})
            </span>
            {canWrite && prompts.some((x) => x.name === p.name) ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  importOne.mutate(p.name);
                }}
                disabled={importOne.isPending}
              >
                Importar como versión publicada
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Callout>
  );
}

function PromptCard({
  p,
  tenantId,
  data,
  notice,
}: {
  p: PromptSummary;
  tenantId: string;
  data: PromptsResponse;
  /** Aviso bajo la descripción (p. ej. que este texto no se usa en el flujo actual). */
  notice?: string | undefined;
}) {
  const [open, setOpen] = useState<'edit' | 'history' | null>(null);
  const writable = data.canWrite && p.editable;
  const status = p.published
    ? `Versión ${String(p.published.version)} · ${fmtDateTime(p.published.externalUpdatedAt ?? p.published.publishedAt)}${
        p.published.origin === 'panel'
          ? ` · ${PUSH_LABEL[p.published.legacyPushStatus ?? 'skipped'] ?? ''}`
          : ' · En uso'
      }`
    : 'Todavía sin versión publicada';
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 basis-72">
          <CardTitle icon={<ScrollText />} className="flex-wrap">
            {p.label}
            {p.draft ? <Badge variant="warning">Borrador v{p.draft.version}</Badge> : null}
            {!p.editable ? (
              <Badge variant="outline">
                <Lock className="size-3" aria-hidden /> Solo lectura
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>{p.description}</CardDescription>
          {notice ? (
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="warning">No se usa</Badge>
              {notice}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-muted-foreground">{status}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={open === 'edit' ? 'secondary' : 'outline'}
            size="sm"
            aria-expanded={open === 'edit'}
            onClick={() => {
              setOpen(open === 'edit' ? null : 'edit');
            }}
          >
            <FileText /> {writable ? 'Editar' : 'Ver texto'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={p.versions === 0}
            aria-expanded={open === 'history'}
            onClick={() => {
              setOpen(open === 'history' ? null : 'history');
            }}
          >
            <History /> Versiones ({p.versions})
          </Button>
        </div>
      </CardHeader>
      {open ? (
        <CardContent className="flex flex-col gap-6">
          {open === 'edit' ? (
            <PromptEditor
              key={`${p.draft?.id ?? ''}${p.published?.id ?? ''}`}
              tenantId={tenantId}
              summary={p}
              canWrite={data.canWrite}
              requirePreview={data.requirePreview}
              onDone={() => {
                setOpen(null);
              }}
            />
          ) : (
            <VersionsList tenantId={tenantId} summary={p} canWrite={data.canWrite} />
          )}
        </CardContent>
      ) : null}
    </Card>
  );
}

function useBotSettings(tenantId: string) {
  return useQuery({
    queryKey: ['bot-settings', tenantId],
    queryFn: () => api<BotSettings>(`/api/tenants/${tenantId}/bot-settings`),
  });
}

/**
 * Interruptor «Responder la primera vez con el dossier adjunto». Vive en la base del stack y el
 * asistente lo lee en cada respuesta: el cambio vale para la siguiente, sin reiniciar nada.
 */
export function FirstContactToggle({ tenantId }: { tenantId: string }) {
  const q = useBotSettings(tenantId);
  const qc = useQueryClient();
  const id = useId();
  const set = useMutation({
    mutationFn: (next: boolean) =>
      api<BotSettings>(`/api/tenants/${tenantId}/bot-settings`, {
        method: 'PUT',
        body: { firstContactEnabled: next, expected: q.data?.firstContactEnabled ?? null },
      }),
    onSuccess: (data) => {
      qc.setQueryData(['bot-settings', tenantId], data);
      void qc.invalidateQueries({ queryKey: ['documents', tenantId] });
      toast.success(
        data.firstContactEnabled
          ? 'Activada: la próxima primera respuesta a alguien interesado sale con el dossier.'
          : 'Desactivada: las primeras respuestas siguen el camino normal del asistente, sin dossier.',
      );
    },
    onError: (err) => {
      toast.error(errorMessage(err));
      void q.refetch();
    },
  });
  const data = q.data;
  if (!data?.available) return null;
  const on = data.effective === true;
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 basis-72">
          <CardTitle icon={<Paperclip />}>
            Responder la primera vez con el dossier adjunto
          </CardTitle>
          <CardDescription>
            {data.mode === 'bot_answer'
              ? `Cuando alguien responde por primera vez con interés, el asistente le contesta con la base de conocimiento (siguiendo las «Instrucciones de la primera respuesta con dossier») y adjunta el dossier.`
              : 'Cuando alguien responde por primera vez pidiendo información, se le envía la «Respuesta de primer contacto con dossier» con el dossier adjunto.'}{' '}
            Apagado, esas respuestas siguen el camino normal del asistente, sin adjunto.
          </CardDescription>
          <p className="mt-1 text-xs text-muted-foreground">
            {data.firstContactEnabled === null
              ? 'Todavía se rige por la configuración inicial del motor.'
              : `Último cambio: ${data.updatedBy?.replace(/^panel:/, '') ?? 'sin dato'}${
                  data.updatedAt ? ` · ${fmtDateTime(data.updatedAt)}` : ''
                }. Vale desde la próxima respuesta.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={id}
            checked={on}
            disabled={!data.canWrite || set.isPending}
            onCheckedChange={(v) => {
              set.mutate(v);
            }}
          />
          <Label htmlFor={id}>{on ? 'Activada' : 'Desactivada'}</Label>
        </div>
      </CardHeader>
    </Card>
  );
}

function PromptsTab({ tenantId }: { tenantId: string }) {
  const q = usePrompts(tenantId);
  const docs = useDocuments(tenantId);
  // Con «Respuesta del asistente + dossier» el texto fijo de primer contacto queda guardado pero no se envía.
  const noticeFor = (p: PromptSummary): string | undefined =>
    p.name === 'first_contact' && docs.data?.dossierBot?.firstContactMode === 'bot_answer'
      ? `El primer contacto se contesta con «${BOT_ANSWER_FLOW}» (lo escribe el «Asistente de respuestas» con la base de conocimiento y va el dossier adjunto).`
      : undefined;
  const data = q.data;
  const used = data ? usedPrompts(data) : [];
  // Empresa sin nada importado todavía: se ofrecen los textos editables para empezar de cero.
  const shown = data
    ? used.length > 0
      ? used
      : data.prompts.filter(
          (p) => p.editable && p.name !== 'answerer' && p.name !== 'first_reply_instructions',
        )
    : [];
  return (
    <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
      {data ? (
        <div className="flex flex-col gap-4">
          {!data.legacyWritesEnabled ? (
            <Callout tone="info" icon={<Info />} title="Los cambios todavía no llegan al asistente">
              Lo que publiques queda guardado aquí, con su historial, pero el asistente sigue usando
              la versión actual hasta que se active la conexión para esta empresa.
            </Callout>
          ) : null}
          {used.length === 0 ? (
            <Callout tone="info" icon={<Info />} title="Todavía no hay textos de esta empresa">
              Los textos que usa la IA se traen solos desde el motor cada pocos minutos. Si esta
              empresa es nueva, puedes escribirlos aquí.
            </Callout>
          ) : null}
          <LegacyStatus tenantId={tenantId} canWrite={data.canWrite} prompts={data.prompts} />
          {used.length > 0
            ? PIPELINE.map((step) => {
                const items = shown.filter((p) => step.names.includes(p.name));
                if (items.length === 0) return null;
                return (
                  <section key={step.title} className="flex flex-col gap-3">
                    <h2 className="text-sm font-semibold text-muted-foreground">{step.title}</h2>
                    {step.names.includes('answerer') ? (
                      <FirstContactToggle tenantId={tenantId} />
                    ) : null}
                    {items.map((p) => (
                      <PromptCard
                        key={p.name}
                        p={p}
                        tenantId={tenantId}
                        data={data}
                        notice={noticeFor(p)}
                      />
                    ))}
                  </section>
                );
              })
            : shown.map((p) => (
                <PromptCard
                  key={p.name}
                  p={p}
                  tenantId={tenantId}
                  data={data}
                  notice={noticeFor(p)}
                />
              ))}
        </div>
      ) : null}
    </Loadable>
  );
}

// ------------------------------------------------------------------ KB

const KB_PAGE = 100;

/** Empresa sin asistente de respuestas: se explica qué hace su IA en vez de mostrar un vacío. */
function NoBotExplanation({
  tenantId,
  tenantName,
  what,
}: {
  tenantId: string;
  tenantName: string;
  what: 'kb' | 'dossier';
}) {
  const q = usePrompts(tenantId);
  const used = q.data ? usedPrompts(q.data) : [];
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={what === 'kb' ? <BookOpen /> : <FileText />}>
          {what === 'kb'
            ? `${tenantName} no tiene base de conocimiento`
            : `${tenantName} no envía dossier`}
        </CardTitle>
        <CardDescription>
          {what === 'kb'
            ? 'La base de conocimiento es lo que consulta el asistente para contestar a los contactos que responden. '
            : 'El dossier es el PDF que el asistente adjunta cuando alguien responde pidiendo información. '}
          {tenantName} no tiene asistente de respuestas: hoy su IA califica tiendas y personaliza el
          primer email.
        </CardDescription>
      </CardHeader>
      {used.length > 0 ? (
        <CardContent>
          <p className="mb-2 text-sm">Estos son los textos que usa (pestaña «Textos de la IA»):</p>
          <ul className="flex flex-col gap-2">
            {used.map((p) => (
              <li key={p.name} className="rounded-md border p-3 text-sm">
                <strong>{p.label}</strong>
                <p className="text-muted-foreground">{p.description}</p>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            Si quieres que {tenantName} responda sola a los contactos, habla con tu contacto en
            Metamindia.
          </p>
        </CardContent>
      ) : null}
    </Card>
  );
}

function hasReplyBot(data: PromptsResponse | undefined): boolean {
  return (data ? usedPrompts(data) : []).some(
    (p) =>
      p.name === 'answerer' || p.name === 'first_contact' || p.name === 'first_reply_instructions',
  );
}

type KbChunk = KbResponse['chunks'][number];

/** Campos de un fragmento (apartado con sugerencias de los que ya existen + texto con contador). */
function KbChunkFields({
  section,
  text,
  sections,
  onSection,
  onText,
}: {
  section: string;
  text: string;
  sections: string[];
  onSection: (v: string) => void;
  onText: (v: string) => void;
}) {
  const sectionId = useId();
  const textId = useId();
  const listId = useId();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={sectionId}>Apartado</Label>
        <Input
          id={sectionId}
          list={listId}
          value={section}
          maxLength={KB_SECTION_MAX_CHARS}
          required
          placeholder="Elige uno existente o escribe uno nuevo"
          onChange={(e) => {
            onSection(e.target.value);
          }}
        />
        <datalist id={listId}>
          {sections.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label htmlFor={textId}>Texto</Label>
          <span
            className={cn(
              'text-xs tabular-nums text-muted-foreground',
              text.length > KB_CHUNK_MAX_CHARS && 'text-destructive',
            )}
          >
            {text.length} / {KB_CHUNK_MAX_CHARS}
          </span>
        </div>
        <Textarea
          id={textId}
          value={text}
          rows={6}
          required
          onChange={(e) => {
            onText(e.target.value);
          }}
        />
      </div>
    </div>
  );
}

function useKbRefresh(tenantId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['kb', tenantId] });
    void qc.invalidateQueries({ queryKey: ['kb-revisions', tenantId] });
  };
}

function KbAddChunk({ tenantId, sections }: { tenantId: string; sections: string[] }) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('');
  const [text, setText] = useState('');
  const refresh = useKbRefresh(tenantId);
  const add = useMutation({
    mutationFn: () =>
      api<KbWriteResponse>(`/api/tenants/${tenantId}/kb/chunks`, {
        method: 'POST',
        body: { section: section.trim(), text: text.trim() },
      }),
    onSuccess: () => {
      toast.success('Fragmento añadido: el asistente ya lo usa desde la próxima respuesta.');
      setOpen(false);
      setSection('');
      setText('');
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  if (!open) {
    return (
      <div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpen(true);
          }}
        >
          <BookOpen /> Añadir fragmento
        </Button>
      </div>
    );
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={<BookOpen />}>Nuevo fragmento</CardTitle>
        <CardDescription>
          Escribe el dato tal cual quieres que lo diga el asistente: responde solo con lo que esté
          escrito aquí.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <KbChunkFields
            section={section}
            text={text}
            sections={sections}
            onSection={setSection}
            onText={setText}
          />
          <div className="flex gap-2">
            <Button
              type="submit"
              size="sm"
              disabled={add.isPending || !section.trim() || !text.trim()}
            >
              <Save /> {add.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

const KB_ACTION_LABEL = { create: 'Añadido', update: 'Editado', delete: 'Borrado' } as const;

function KbChunkHistory({ tenantId, chunkId }: { tenantId: string; chunkId: string }) {
  const q = useQuery({
    queryKey: ['kb-revisions', tenantId, chunkId],
    queryFn: () =>
      api<KbRevisionsResponse>(
        `/api/tenants/${tenantId}/kb/revisions?chunkId=${encodeURIComponent(chunkId)}`,
      ),
  });
  const revs = q.data?.revisions ?? [];
  return (
    <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
      {revs.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Sin cambios desde el panel: es el texto de la carga inicial.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {revs.map((r) => (
            <li key={r.id} className="flex flex-col gap-1">
              <p className="text-xs text-muted-foreground">
                {KB_ACTION_LABEL[r.action]} por {r.actorEmail ?? 'sistema'} ·{' '}
                {fmtDateTime(r.createdAt)}
              </p>
              {r.action === 'update' ? (
                <DiffView before={r.textBefore ?? ''} after={r.textAfter ?? ''} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Loadable>
  );
}

function KbChunkActions({
  tenantId,
  chunk,
  sections,
}: {
  tenantId: string;
  chunk: KbChunk;
  sections: string[];
}) {
  const [mode, setMode] = useState<'view' | 'edit' | 'history'>('view');
  const [section, setSection] = useState(chunk.section);
  const [text, setText] = useState(chunk.text);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refresh = useKbRefresh(tenantId);
  const url = `/api/tenants/${tenantId}/kb/chunks/${encodeURIComponent(chunk.id)}`;
  const save = useMutation({
    mutationFn: () =>
      api<KbWriteResponse>(url, {
        method: 'PATCH',
        body: { text: text.trim(), section: section.trim(), expectedText: chunk.text },
      }),
    onSuccess: () => {
      toast.success('Fragmento guardado: el asistente ya usa el texto nuevo.');
      setMode('view');
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div className="flex flex-col gap-3 border-t px-3 py-2">
      {mode === 'edit' ? (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <KbChunkFields
            section={section}
            text={text}
            sections={sections}
            onSection={setSection}
            onText={setText}
          />
          {text !== chunk.text ? <DiffView before={chunk.text} after={text} /> : null}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={save.isPending}>
              <Save /> {save.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setText(chunk.text);
                setSection(chunk.section);
                setMode('view');
              }}
            >
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <p className="whitespace-pre-line">{chunk.text}</p>
      )}
      {mode === 'history' ? <KbChunkHistory tenantId={tenantId} chunkId={chunk.id} /> : null}
      {mode !== 'edit' ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setMode('edit');
            }}
          >
            <FileText /> Editar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={mode === 'history'}
            onClick={() => {
              setMode(mode === 'history' ? 'view' : 'history');
            }}
          >
            <History /> Historial
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConfirmDelete(true);
            }}
          >
            <Trash2 /> Borrar
          </Button>
        </div>
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="¿Borrar este fragmento?"
        description="El asistente deja de usarlo desde la próxima respuesta. El texto no se pierde: queda archivado y en el historial."
        confirmLabel="Borrar"
        onConfirm={async () => {
          await api<KbWriteResponse>(url, { method: 'DELETE', body: { expectedText: chunk.text } });
          toast.success('Fragmento borrado (queda archivado).');
          refresh();
        }}
      />
    </div>
  );
}

function KbTab({ tenantId, tenantName }: { tenantId: string; tenantName: string }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [offset, setOffset] = useState(0);
  const searchId = useId();
  const prompts = usePrompts(tenantId);
  const query = useQuery({
    queryKey: ['kb', tenantId, term, offset],
    queryFn: () =>
      api<KbResponse>(
        `/api/tenants/${tenantId}/kb?limit=${String(KB_PAGE)}&offset=${String(offset)}${term ? `&q=${encodeURIComponent(term)}` : ''}`,
      ),
  });
  const data = query.data;
  const groups = useMemo(() => {
    const out: { title: string; chunks: KbResponse['chunks'] }[] = [];
    for (const c of data?.chunks ?? []) {
      const g = out.at(-1);
      if (g?.title === c.section) g.chunks.push(c);
      else out.push({ title: c.section, chunks: [c] });
    }
    return out;
  }, [data]);
  if (data && !data.available && !prompts.isLoading && !hasReplyBot(prompts.data)) {
    return <NoBotExplanation tenantId={tenantId} tenantName={tenantName} what="kb" />;
  }
  return (
    <Loadable isLoading={query.isLoading} error={query.error} onRetry={() => void query.refetch()}>
      {data && !data.available ? (
        <EmptyState
          icon={<BookOpen />}
          title="No se pudo leer la base de conocimiento"
          description={data.message ?? ''}
        />
      ) : data ? (
        <div className="flex flex-col gap-4">
          {data.editable ? (
            <Callout tone="info" icon={<Info />}>
              Esto es lo que consulta el asistente para responder. Lo que cambies o añadas vale
              desde la próxima respuesta, y cada versión anterior queda guardada (se puede ver en
              «Historial»). Un fragmento es una idea completa: el asistente solo cita lo que dice
              literalmente.
            </Callout>
          ) : (
            <Callout tone="info" icon={<Info />}>
              Esto es lo que consulta el asistente para responder (solo lectura).{' '}
              {data.readOnlyReason ??
                'Si hay algo que cambiar o añadir, pídeselo a tu contacto en Metamindia.'}
            </Callout>
          )}
          {data.editable ? (
            <KbAddChunk tenantId={tenantId} sections={data.sections.map((s) => s.title)} />
          ) : null}
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">
              {data.sections.reduce((n, s) => n + s.chunks, 0)} fragmentos
            </strong>{' '}
            en {data.sections.length} apartados
            {data.lastUpdatedAt ? ` · actualizada el ${fmtDateTime(data.lastUpdatedAt)}` : ''}
          </p>
          <form
            role="search"
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setTerm(q);
              setOffset(0);
            }}
          >
            <Label htmlFor={searchId} className="sr-only">
              Buscar en la base de conocimiento
            </Label>
            <Input
              id={searchId}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
              }}
              placeholder="Buscar, p. ej. «pedido mínimo»"
            />
            <Button type="submit" variant="outline">
              Buscar
            </Button>
          </form>
          {term ? (
            <p className="text-sm text-muted-foreground">
              {data.total} fragmentos con «{term}»
            </p>
          ) : null}
          {groups.length === 0 ? (
            <EmptyState icon={<BookOpen />} title="Nada coincide con la búsqueda" />
          ) : null}
          {groups.map((g, gi) => (
            <section key={`${g.title}-${String(gi)}`} className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">
                {g.title}{' '}
                <span className="font-normal text-muted-foreground">
                  · {data.sections.find((x) => x.title === g.title)?.chunks ?? g.chunks.length}
                </span>
              </h3>
              <ul className="flex flex-col gap-2">
                {g.chunks.map((c) => (
                  <li key={c.id} className="rounded-md border text-sm">
                    <details className="group">
                      <summary className="cursor-pointer list-none rounded-md p-3 hover:bg-muted/50">
                        <span className="font-medium">{c.title}</span>
                        <span className="mt-0.5 block text-muted-foreground">{c.preview}</span>
                      </summary>
                      {data.editable ? (
                        <KbChunkActions
                          key={`${c.id}:${c.section}:${c.text}`}
                          tenantId={tenantId}
                          chunk={c}
                          sections={data.sections.map((s) => s.title)}
                        />
                      ) : (
                        <p className="border-t px-3 py-2 whitespace-pre-line">{c.text}</p>
                      )}
                    </details>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {data.total > KB_PAGE ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={offset === 0}
                onClick={() => {
                  setOffset(Math.max(0, offset - KB_PAGE));
                }}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={offset + KB_PAGE >= data.total}
                onClick={() => {
                  setOffset(offset + KB_PAGE);
                }}
              >
                Siguiente
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Loadable>
  );
}

// ------------------------------------------------------------------ dossier

function DocumentsTab({
  tenantId,
  tenantName,
  canWrite,
}: {
  tenantId: string;
  tenantName: string;
  canWrite: boolean;
}) {
  const isAgency = canAccess(useMe(), 'ops:read');
  const prompts = usePrompts(tenantId);
  const qc = useQueryClient();
  const fileId = useId();
  const noteId = useId();
  const [busy, setBusy] = useState(false);
  const q = useDocuments(tenantId);
  const activate = useMutation({
    mutationFn: (v: { key: string; id: string }) =>
      api(`/api/tenants/${tenantId}/documents/${v.key}/versions/${v.id}/activate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Versión activa cambiada');
      void qc.invalidateQueries({ queryKey: ['documents', tenantId] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const upload = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const file = f.get('file');
    if (!(file instanceof File) || file.size === 0) {
      toast.error('Elige un archivo');
      return;
    }
    if (file.size > DOCUMENT_MAX_BYTES) {
      toast.error(`El archivo supera ${fmtBytes(DOCUMENT_MAX_BYTES)}`);
      return;
    }
    setBusy(true);
    try {
      await api(`/api/tenants/${tenantId}/documents/dossier/versions`, {
        method: 'POST',
        body: {
          filename: file.name,
          mime: file.type || 'application/pdf',
          contentBase64: await fileToBase64(file),
          note: fd(f, 'note') || undefined,
        },
      });
      toast.success('Versión nueva del dossier subida');
      form.reset();
      void qc.invalidateQueries({ queryKey: ['documents', tenantId] });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const empty = (q.data?.documents ?? []).length === 0 && (q.data?.sent ?? []).length === 0;
  if (q.data && empty && !prompts.isLoading && !hasReplyBot(prompts.data)) {
    return <NoBotExplanation tenantId={tenantId} tenantName={tenantName} what="dossier" />;
  }
  return (
    <Loadable isLoading={q.isLoading} error={q.error} onRetry={() => void q.refetch()}>
      <div className="flex flex-col gap-6">
        {isAgency ? (
          <Callout tone="info" icon={<Info />}>
            El asistente baja de aquí la versión en uso cada vez que envía el dossier (y guarda una
            copia de respaldo en n8n, que se actualiza sola cada hora). Ya no hace falta
            <code className="mx-1">upload-dossier.sh</code>: subir o activar una versión es
            suficiente.
          </Callout>
        ) : null}
        {canWrite ? (
          <Card>
            <CardHeader>
              <CardTitle icon={<Upload />}>Subir versión nueva del dossier</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => void upload(e)} className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                  <Label htmlFor={fileId}>Archivo (PDF)</Label>
                  <Input
                    id={fileId}
                    name="file"
                    type="file"
                    accept="application/pdf,image/png,image/jpeg"
                    required
                  />
                </div>
                <div className="flex min-w-48 flex-1 flex-col gap-1">
                  <Label htmlFor={noteId}>Nota</Label>
                  <Input id={noteId} name="note" placeholder="Qué cambió" maxLength={500} />
                </div>
                <Button type="submit" disabled={busy}>
                  <CloudUpload /> {busy ? 'Subiendo…' : 'Subir'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}
        {(q.data?.documents ?? []).length === 0 ? (
          <EmptyState icon={<FileText />} title="Todavía no hay documentos cargados" />
        ) : (
          q.data?.documents.map((d) => (
            <Card key={d.id}>
              <CardHeader>
                <CardTitle icon={<FileText />}>{d.title}</CardTitle>
                <CardDescription>
                  Es el PDF que el asistente adjunta cuando alguien responde pidiendo información.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {d.key === 'dossier' ? (
                  <DossierBotStatus doc={d} usage={q.data.dossierBot} tenantName={tenantName} />
                ) : null}
                <DocumentPreview doc={d} />
                <h3 className="text-sm font-semibold">Versiones</h3>
                <ol className="flex flex-col gap-2">
                  {d.versions.map((v) => (
                    <li
                      key={v.id}
                      className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm"
                    >
                      <strong>v{v.version}</strong>
                      {v.active ? <Badge variant="success">En uso</Badge> : null}
                      <a
                        className="text-primary hover:underline"
                        href={`/api/attachments/${v.attachmentId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {v.filename}
                      </a>
                      <span className="text-xs text-muted-foreground">
                        {fmtBytes(v.sizeBytes)} · {v.uploadedByEmail ?? 'importado de n8n'} ·{' '}
                        {fmtDateTime(v.createdAt)} · enviado {v.timesSent}{' '}
                        {v.timesSent === 1 ? 'vez' : 'veces'}
                      </span>
                      {v.note ? (
                        <span className="basis-full text-xs text-muted-foreground">{v.note}</span>
                      ) : null}
                      {canWrite && !v.active ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-auto"
                          onClick={() => {
                            activate.mutate({ key: d.key, id: v.id });
                          }}
                        >
                          Activar
                        </Button>
                      ) : null}
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))
        )}
        {(q.data?.sent ?? []).length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle icon={<Paperclip />}>Archivos efectivamente enviados</CardTitle>
              <CardDescription>Lo que salió adjunto en los mails (versión exacta).</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="flex flex-col gap-1 text-sm">
                {q.data?.sent.map((s) => (
                  <li key={s.id}>
                    <a
                      className="text-primary hover:underline"
                      href={`/api/attachments/${s.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {s.filename}
                    </a>{' '}
                    <span className="text-xs text-muted-foreground">
                      {fmtBytes(s.sizeBytes)} · {s.timesSent} envíos · último{' '}
                      {fmtDateTime(s.lastSentAt)} · sha256 {s.sha256.slice(0, 10)}…
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>
    </Loadable>
  );
}

/**
 * ¿El asistente usa este dossier? Lo dice el último latido del sync del dossier de n8n: la llave
 * de la respuesta de primer contacto vive en n8n, no en la plataforma.
 */
export function DossierBotStatus({
  doc,
  usage,
  tenantName,
}: {
  doc: DocumentsResponse['documents'][number];
  usage: DocumentsResponse['dossierBot'];
  tenantName: string;
}) {
  const active = doc.versions.find((v) => v.active);
  const which = active
    ? `la versión ${String(active.version)} (${active.filename})`
    : 'la versión en uso';
  if (!usage) {
    return (
      <Callout tone="info" icon={<Bot />} title="Sin datos del asistente todavía">
        El asistente de {tenantName} aún no ha informado si envía este dossier. Lo comprueba solo
        cada hora; vuelve a mirar más tarde.
      </Callout>
    );
  }
  const backupBehind =
    usage.firstContactEnabled &&
    active !== undefined &&
    usage.localSha256 !== null &&
    usage.localSha256 !== active.sha256;
  return (
    <div className="flex flex-col gap-2">
      {usage.firstContactEnabled ? (
        <Callout
          tone="success"
          icon={<Bot />}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="success">En uso por el asistente</Badge>
            </span>
          }
        >
          {usage.firstContactMode === 'bot_answer' ? (
            <>
              {BOT_ANSWER_FLOW}: cuando alguien responde pidiendo información, el asistente de{' '}
              {tenantName} le contesta con la base de conocimiento y adjunta {which}.
            </>
          ) : (
            <>
              Cuando alguien responde pidiendo información, el asistente de {tenantName} le contesta
              con el email de bienvenida y adjunta {which}.
            </>
          )}{' '}
          Si subes o activas otra versión, la siguiente respuesta ya sale con esa.
        </Callout>
      ) : (
        <Callout
          tone="warning"
          icon={<Bot />}
          title={
            <span className="flex flex-wrap items-center gap-2">
              <Badge variant="warning">Cargado, sin usar todavía</Badge>
            </span>
          }
        >
          El dossier está guardado, pero la respuesta automática de primer contacto de {tenantName}{' '}
          todavía no está activada, así que el asistente no lo envía a nadie. Cuando se active,
          adjuntará {which}.{' '}
          {usage.firstContactMode === 'bot_answer'
            ? `Será la «${BOT_ANSWER_FLOW}»: la escribe el «Asistente de respuestas» con la base de conocimiento. Se activa con «Responder la primera vez con el dossier adjunto», en la pestaña «Textos de la IA».`
            : 'Antes de activarla, revisa el texto «Respuesta de primer contacto con dossier»; se activa con «Responder la primera vez con el dossier adjunto», los dos en la pestaña «Textos de la IA».'}
        </Callout>
      )}
      <p className="text-xs text-muted-foreground">
        Último dato del asistente: {fmtDateTime(usage.reportedAt)}.
        {usage.stale ? ' Hace más de 3 horas que no informa: puede estar desactualizado.' : null}
        {backupBehind
          ? ` Su copia de respaldo todavía es la versión ${String(usage.localVersion ?? '?')}; se actualiza sola en menos de una hora.`
          : null}
        {usage.lastSyncFailed && usage.firstContactEnabled
          ? ' La última actualización de la copia de respaldo falló: si la plataforma no respondiera, podría salir una versión anterior.'
          : null}
      </p>
    </div>
  );
}

/** Vista previa del archivo en uso + descarga. */
function DocumentPreview({ doc }: { doc: DocumentsResponse['documents'][number] }) {
  const [expanded, setExpanded] = useState(false);
  const active = doc.versions.find((v) => v.active) ?? doc.versions[0];
  if (!active) return null;
  const href = `/api/attachments/${active.attachmentId}`;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <strong>Versión {active.version} en uso</strong>
        <span className="text-muted-foreground">
          {active.filename} · {fmtBytes(active.sizeBytes)} · {fmtDateTime(active.createdAt)}
        </span>
        <span className="ml-auto flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setExpanded(true);
            }}
          >
            <Maximize2 /> Ampliar
          </Button>
          <a
            href={href}
            download={active.filename}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium hover:bg-muted"
          >
            <Download className="size-4" aria-hidden /> Descargar
          </a>
        </span>
      </div>
      <ViewerDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={doc.title}
        description={`Versión ${String(active.version)} · ${active.filename}`}
        download={{ href, filename: active.filename }}
      >
        {active.mime === 'application/pdf' ? (
          <iframe title={`${doc.title} (ampliado)`} src={href} className="size-full" />
        ) : (
          <img src={href} alt={doc.title} className="size-full object-contain" />
        )}
      </ViewerDialog>
      {active.mime === 'application/pdf' ? (
        <iframe
          title={`Vista previa de ${doc.title}`}
          src={href}
          className="h-[28rem] w-full rounded-md border bg-card"
        />
      ) : (
        <img
          src={href}
          alt={`Vista previa de ${doc.title}`}
          className="max-h-[28rem] rounded-md border"
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ página

export function PromptsPage() {
  const { tenant, candidates, setPicked, can } = useTenantPick('prompts:read');
  return (
    <Guard requires="prompts:read">
      <PageHeader
        title="Asistente IA"
        description="Qué hace la IA de tu empresa, con qué textos trabaja y qué sabe de tu negocio. Los cambios se prueban en borrador antes de publicarlos y siempre se puede volver a una versión anterior."
        actions={<TenantPicker candidates={candidates} value={tenant?.id} onChange={setPicked} />}
      />
      {!tenant ? (
        <EmptyState
          icon={<FileText />}
          title="Sin empresas"
          description="No hay empresas con textos de IA visibles en este alcance."
        />
      ) : (
        <div key={tenant.id}>
          <AiOverview tenantId={tenant.id} tenantName={tenant.name} />
          <Tabs defaultValue="prompts">
            <TabsList className="mb-4">
              <TabsTrigger value="prompts">Textos de la IA</TabsTrigger>
              <TabsTrigger value="kb">Base de conocimiento</TabsTrigger>
              <TabsTrigger value="dossier">Dossier</TabsTrigger>
            </TabsList>
            <TabsContent value="prompts">
              <PromptsTab tenantId={tenant.id} />
            </TabsContent>
            <TabsContent value="kb">
              <KbTab tenantId={tenant.id} tenantName={tenant.name} />
            </TabsContent>
            <TabsContent value="dossier">
              <DocumentsTab
                tenantId={tenant.id}
                tenantName={tenant.name}
                canWrite={can('prompts:write')}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Guard>
  );
}
